// Versioned IndexedDB store for the development sticker scanner.
//
// This is engine-internal wiring. It is NOT part of the public gameplay import
// surface — gameplay code must never import from here (enforced by
// scripts/check-import-boundary.mjs and the ESLint gameplay guard).
//
// Everything is same-origin and local: a single IndexedDB database on the
// current origin. No network, no Firebase, no camera, no cookies, no
// localStorage/sessionStorage. Records are structured-clone-safe plain objects.
//
// SSR safety: nothing here touches IndexedDB at module-evaluation time. The
// database is opened lazily the first time a method needs it, and every entry
// point resolves the IDBFactory through resolveIndexedDB(), which throws a typed
// StickerError('storage_unavailable') when IndexedDB is missing (e.g. during
// server rendering) or blocked.

import type { JsonObject, ProjectTag, StickerCode } from '../sticker_types';
import { StickerError, UNASSIGNED_TAG } from '../sticker_types';
import { DEMO_PROJECT } from './demo_project';

export const DB_NAME = 'echo-engine-sticker-dev';
// v2 introduced tags. v3 introduced the JSON-backed robot demo catalog.
// v4 refreshes existing demo databases with the expanded sticker-code catalog.
export const DB_VERSION = 4;

export const STORE_CODES = 'codes';
export const STORE_SAVES = 'saves';
export const STORE_META = 'meta';
export const STORE_TAGS = 'tags';

export const META_NEXT_CODE = 'nextCode';

/** Stable id/name of the default tag applied to untagged/legacy codes. */
export const DEFAULT_TAG_ID = UNASSIGNED_TAG.id;
export const DEFAULT_TAG_NAME = UNASSIGNED_TAG.name;

// A project tag catalog entry. `id` is stable identity; `name` is the trimmed,
// case-insensitively-unique display label. Mirrors the shared ProjectTag shape.
export interface TagRecord {
  id: string;
  name: string;
}

// A physical sticker code and where its save lives. `fault` reproduces the
// deterministic hardware-failure fixture. `used` records whether the code has
// been activated (its save established) via a scan yet. `tagId` references a
// TagRecord (the code's batch/project tag); legacy records without it resolve
// to the default Unassigned tag.
export interface CodeRecord {
  code: StickerCode;
  saveId: string | null;
  used: boolean;
  fault: boolean;
  tagId: string;
}

// A free-form save game. `data` is arbitrary gameplay JSON.
export interface SaveRecord {
  saveId: string;
  data: JsonObject;
  createdAt: number;
  updatedAt: number;
}

// Deterministic robot demo fixtures derived from the checked-in JSON asset.
// Every code starts activated/unused with no save. Gameplay creates saves and
// links later through StickerService, while reset restores this exact catalog.
export const FIXTURE_CODES: StickerCode[] = DEMO_PROJECT.codes.map(
  ({ code }) => code
);

export const FIXTURE_TAGS: TagRecord[] = DEMO_PROJECT.tags.map((tag) => ({
  id: tag,
  name: tag,
}));

const FIXTURE_SEED: CodeRecord[] = DEMO_PROJECT.codes.map(({ code, tag }) => ({
  code,
  saveId: null,
  used: false,
  fault: false,
  tagId: tag,
}));

/** Default save game established when a fixture is first activated. */
export function seedSaveData(_code: StickerCode): JsonObject {
  return {
    playerName: 'Guest',
    score: 0,
    tokens: [],
    level: 1,
    checkpoint: 'start',
  };
}

/**
 * Resolve the IndexedDB factory for the current environment. Prefer an injected
 * factory (tests), then the browser global. Throws a typed error otherwise so
 * SSR / unsupported / blocked environments surface cleanly.
 */
export function resolveIndexedDB(factory?: IDBFactory): IDBFactory {
  const idb =
    factory ??
    (typeof globalThis !== 'undefined'
      ? (globalThis as { indexedDB?: IDBFactory }).indexedDB
      : undefined);
  if (!idb) {
    throw new StickerError(
      'storage_unavailable',
      'IndexedDB is not available in this environment (server render or unsupported browser).'
    );
  }
  return idb;
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new StickerError(
          'storage_unavailable',
          request.error?.message ?? 'IndexedDB request failed'
        )
      );
  });
}

function promisifyTransaction(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () =>
      reject(
        new StickerError(
          'storage_unavailable',
          tx.error?.message ?? 'IndexedDB transaction aborted'
        )
      );
    tx.onerror = () =>
      reject(
        new StickerError(
          'storage_unavailable',
          tx.error?.message ?? 'IndexedDB transaction failed'
        )
      );
  });
}

/**
 * Open (and, on first creation, seed) the sticker database. Seeding happens
 * only inside onupgradeneeded, i.e. exactly once when the database is created
 * (or when DB_VERSION increases and a migration runs).
 */
export function openStickerDb(factory?: IDBFactory): Promise<IDBDatabase> {
  const idb = resolveIndexedDB(factory);
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = idb.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(
        new StickerError(
          'storage_unavailable',
          err instanceof Error ? err.message : 'Failed to open IndexedDB'
        )
      );
      return;
    }

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const tx = request.transaction!;
      const oldVersion = event.oldVersion;

      if (!db.objectStoreNames.contains(STORE_CODES)) {
        db.createObjectStore(STORE_CODES, { keyPath: 'code' });
      }
      if (!db.objectStoreNames.contains(STORE_SAVES)) {
        db.createObjectStore(STORE_SAVES, { keyPath: 'saveId' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_TAGS)) {
        db.createObjectStore(STORE_TAGS, { keyPath: 'id' });
      }

      const tagStore = tx.objectStore(STORE_TAGS);
      const codeStore = tx.objectStore(STORE_CODES);

      if (oldVersion < 4) {
        // Demo mode owns a fixed local catalog. Clear older fixture/catalog
        // data and its saves, then install the current JSON-backed records.
        codeStore.clear();
        tx.objectStore(STORE_SAVES).clear();
        tx.objectStore(STORE_META).clear();
        tagStore.clear();
        for (const tag of FIXTURE_TAGS) tagStore.put({ ...tag });
        for (const record of FIXTURE_SEED) codeStore.put({ ...record });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // If another tab requests a version bump, close so it is not blocked.
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onblocked = () =>
      reject(
        new StickerError(
          'storage_unavailable',
          'IndexedDB open is blocked by another connection.'
        )
      );

    request.onerror = () => {
      // A Next.js hot-reload can leave an older Stage singleton alive after a
      // newer bundle has upgraded this database. Open its current version rather
      // than failing with VersionError; normal upgrades still use DB_VERSION.
      if (request.error?.name === 'VersionError') {
        const currentRequest = idb.open(DB_NAME);
        currentRequest.onsuccess = () => {
          const db = currentRequest.result;
          db.onversionchange = () => db.close();
          resolve(db);
        };
        currentRequest.onerror = () =>
          reject(
            new StickerError(
              'storage_unavailable',
              currentRequest.error?.message ??
                'Failed to open the current IndexedDB version'
            )
          );
        currentRequest.onblocked = () =>
          reject(
            new StickerError(
              'storage_unavailable',
              'Opening the current IndexedDB version was blocked.'
            )
          );
        return;
      }

      reject(
        new StickerError(
          'storage_unavailable',
          request.error?.message ?? 'Failed to open IndexedDB'
        )
      );
    };
  });
}

// ---- Low-level record helpers (shared by the adapter and dev-data surface) ----

export async function getCodeRecord(
  db: IDBDatabase,
  code: StickerCode
): Promise<CodeRecord | undefined> {
  const tx = db.transaction(STORE_CODES, 'readonly');
  const result = await promisifyRequest<CodeRecord | undefined>(
    tx.objectStore(STORE_CODES).get(code)
  );
  return result;
}

export async function getAllCodeRecords(db: IDBDatabase): Promise<CodeRecord[]> {
  const tx = db.transaction(STORE_CODES, 'readonly');
  return promisifyRequest<CodeRecord[]>(tx.objectStore(STORE_CODES).getAll());
}

export async function putCodeRecord(
  db: IDBDatabase,
  record: CodeRecord
): Promise<void> {
  const tx = db.transaction(STORE_CODES, 'readwrite');
  tx.objectStore(STORE_CODES).put({ ...record });
  await promisifyTransaction(tx);
}

/**
 * Atomically attach a newly scanned code to an established code's save. The
 * established save always wins. A code already committed to another save is
 * never merged implicitly.
 */
export async function linkNewCodeToExistingSave(
  db: IDBDatabase,
  newCode: StickerCode,
  provisionalSaveId: string,
  existingCode: StickerCode
): Promise<{ source: CodeRecord; target: CodeRecord }> {
  const tx = db.transaction([STORE_CODES, STORE_SAVES], 'readwrite');
  const codeStore = tx.objectStore(STORE_CODES);
  const saveStore = tx.objectStore(STORE_SAVES);

  const [source, target, allCodes] = await Promise.all([
    promisifyRequest<CodeRecord | undefined>(codeStore.get(newCode)),
    promisifyRequest<CodeRecord | undefined>(codeStore.get(existingCode)),
    promisifyRequest<CodeRecord[]>(codeStore.getAll()),
  ]);

  if (!source) {
    throw new StickerError('internal', `Unknown new sticker code: ${newCode}`);
  }
  if (!target?.used || !target.saveId) {
    throw new StickerError(
      'collection_conflict',
      'Scan a robot that already belongs to the existing collection.'
    );
  }
  if (newCode === existingCode) {
    throw new StickerError(
      'same_sticker',
      'Scan a different robot that is already in the collection.'
    );
  }
  const linkedSource: CodeRecord = {
    ...source,
    saveId: target.saveId,
    used: true,
  };
  codeStore.put({ ...linkedSource });

  if (
    provisionalSaveId !== target.saveId &&
    !allCodes.some(
      (record) =>
        record.code !== newCode && record.saveId === provisionalSaveId
    )
  ) {
    saveStore.delete(provisionalSaveId);
  }
  if (source.used && source.saveId !== target.saveId) {
    throw new StickerError(
      'collection_conflict',
      'This robot already belongs to a different collection.'
    );
  }

  await promisifyTransaction(tx);
  return { source: linkedSource, target };
}

export async function getSaveRecord(
  db: IDBDatabase,
  saveId: string
): Promise<SaveRecord | undefined> {
  const tx = db.transaction(STORE_SAVES, 'readonly');
  return promisifyRequest<SaveRecord | undefined>(
    tx.objectStore(STORE_SAVES).get(saveId)
  );
}

export async function getAllSaveRecords(db: IDBDatabase): Promise<SaveRecord[]> {
  const tx = db.transaction(STORE_SAVES, 'readonly');
  return promisifyRequest<SaveRecord[]>(tx.objectStore(STORE_SAVES).getAll());
}

export async function putSaveRecord(
  db: IDBDatabase,
  record: SaveRecord
): Promise<void> {
  const tx = db.transaction(STORE_SAVES, 'readwrite');
  tx.objectStore(STORE_SAVES).put({ ...record });
  await promisifyTransaction(tx);
}

export async function getMeta(
  db: IDBDatabase,
  key: string
): Promise<string | null> {
  const tx = db.transaction(STORE_META, 'readonly');
  const row = await promisifyRequest<{ key: string; value: string } | undefined>(
    tx.objectStore(STORE_META).get(key)
  );
  return row?.value ?? null;
}

export async function setMeta(
  db: IDBDatabase,
  key: string,
  value: string | null
): Promise<void> {
  const tx = db.transaction(STORE_META, 'readwrite');
  const store = tx.objectStore(STORE_META);
  if (value === null) {
    store.delete(key);
  } else {
    store.put({ key, value });
  }
  await promisifyTransaction(tx);
}

export async function getAllTagRecords(db: IDBDatabase): Promise<TagRecord[]> {
  const tx = db.transaction(STORE_TAGS, 'readonly');
  return promisifyRequest<TagRecord[]>(tx.objectStore(STORE_TAGS).getAll());
}

export async function getTagRecord(
  db: IDBDatabase,
  id: string
): Promise<TagRecord | undefined> {
  const tx = db.transaction(STORE_TAGS, 'readonly');
  return promisifyRequest<TagRecord | undefined>(
    tx.objectStore(STORE_TAGS).get(id)
  );
}

export async function putTagRecord(
  db: IDBDatabase,
  record: TagRecord
): Promise<void> {
  const tx = db.transaction(STORE_TAGS, 'readwrite');
  tx.objectStore(STORE_TAGS).put({ ...record });
  await promisifyTransaction(tx);
}

/**
 * Resolve a code's `tagId` into a complete ProjectTag using a loaded tag
 * catalog. A blank/missing id resolves to the default Unassigned tag; a
 * non-empty id with no catalog entry falls back to `{id, name: id}` so an
 * orphaned reference still yields a usable, non-destructive result.
 */
export function resolveTag(
  tags: TagRecord[],
  tagId: string | null | undefined
): ProjectTag {
  const id = tagId && tagId.length > 0 ? tagId : DEFAULT_TAG_ID;
  const found = tags.find((t) => t.id === id);
  if (found) return { id: found.id, name: found.name };
  if (id === DEFAULT_TAG_ID) return { id: DEFAULT_TAG_ID, name: DEFAULT_TAG_NAME };
  return { id, name: id };
}

/** Codes that share a save with `code` (excluding `code` itself). */
export function linkedCodesFrom(
  records: CodeRecord[],
  code: StickerCode
): StickerCode[] {
  const self = records.find((r) => r.code === code);
  if (!self || self.saveId === null) return [];
  return records
    .filter((r) => r.code !== code && r.saveId === self.saveId)
    .map((r) => r.code);
}

/**
 * Wipe all stores and re-apply first-run fixtures. Used by the dev-data
 * management surface to reset test data without deleting the database.
 */
export async function resetToFixtures(db: IDBDatabase): Promise<void> {
  const tx = db.transaction(
    [STORE_CODES, STORE_SAVES, STORE_META, STORE_TAGS],
    'readwrite'
  );
  tx.objectStore(STORE_CODES).clear();
  tx.objectStore(STORE_SAVES).clear();
  tx.objectStore(STORE_META).clear();
  tx.objectStore(STORE_TAGS).clear();
  const codeStore = tx.objectStore(STORE_CODES);
  for (const record of FIXTURE_SEED) {
    codeStore.put({ ...record });
  }
  const tagStore = tx.objectStore(STORE_TAGS);
  for (const record of FIXTURE_TAGS) {
    tagStore.put({ ...record });
  }
  await promisifyTransaction(tx);
}
